import * as Apollo from '@apollo/react-hooks'
import * as React from 'react'

import { GenericError, Loader } from 'main/components/common'
import client from 'main/framework/apolloClient'
import { CredentialsFields } from 'main/gql/credentials'
import { Modal } from 'main/ui'
import { UnreachableCaseError } from 'main/util/ts'

import { INSTANT_ENROLL_QUERY } from '../gql/instantEnroll.query'
import { School, User } from '../types'
import Steps, { buildStep, ICoreProps, IExtraProps, IStep, StepType, track } from './Steps'

interface IProps {
  instantEnroll: boolean
  school: School
  title: string
  onAuth: (credentials: CredentialsFields) => Promise<void>
  onComplete: () => void
  onRequestClose: () => void
}

export default function Content(props: IProps) {
  const { title, onRequestClose } = props

  const { data, loading, error } = Apollo.useQuery(INSTANT_ENROLL_QUERY, { client })

  return (
    <Modal title={title} onRequestClose={onRequestClose}>
      {renderModalContent()}
    </Modal>
  )

  function renderModalContent() {
    if (loading) return <Loader />
    if (error || !data) return <GenericError />

    return <Step user={data.user} {...props} />
  }
}

interface IStepProps extends IProps {
  user: User | null
}

function Step(props: IStepProps) {
  const { instantEnroll, school, user, onAuth, onComplete } = props

  const [step, setStep] = React.useState<IStep>(buildStep(StepType.INIT, instantEnroll, user))
  const core: ICoreProps = {
    instantEnroll,
    school,
    changeStep: (newStep?: StepType) => handleChangeStep(newStep),
  }

  return renderStep(step, core, { onAuth, onComplete })

  function handleChangeStep(changeTo?: StepType) {
    const currentStep: StepType = step.current
    const nextStep: StepType | null = changeTo || step.next

    track(currentStep, { schoolId: school.id, userId: user?.id })
    if (!nextStep) return onComplete()

    setStep(buildStep(nextStep, instantEnroll))
  }
}

function renderStep(step: IStep, props: ICoreProps, extraProps: IExtraProps) {
  switch (step.current) {
    case StepType.INIT:
      return <Steps.Initial {...props} />
    case StepType.LOGIN:
      return <Steps.Login {...props} {...extraProps} />
    case StepType.SIGN_UP:
      return <Steps.SignUp {...props} {...extraProps} />
    case StepType.PAYMENT_METHOD:
      return <Steps.PaymentMethod {...props} />
    default:
      throw new UnreachableCaseError(step.current)
  }
}
